import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { InferenceStatusBanner } from '../InferenceStatusBanner';
import PlansPage from '../billing/PlansPage';
import { widgetSnippet, mailtoInstallSteps } from '../../widget/snippet';

/**
 * PHASE-2 guided getting-started wizard (operator-approved 2026-07-11, build to stg).
 * The polished free-trial onboarding layer over the LIVE configure→test→install→publish spine.
 * UXeng owns the UI; eng-1 owns the backend. Reached from the www front door via OAuth →
 * lands here authenticated (no-account routing → /onboarding).
 * MVP scope = the minimum screens to pass AC3's 6 checkpoints:
 *   0 create workspace → 1 pick skill → 2 add KB → 3 test → 4 install → 5 go-live.
 *
 * ZERO-CONFIG invariant (AC3 checkpoint 2): NO key / model / "Connect AI" screen is ever
 * shown. Inference is managed (single shared fleet key); the model is assigned silently at
 * signup and a trial quota is stamped there (F3). The wizard never mentions either.
 *
 * Reuses existing surfaces rather than rebuilding: the test-chat stream, the widget-snippet
 * mechanism, the publish toggle, and InferenceStatusBanner (mounted in the Test step).
 *
 * Two steps consume eng-1 backend contracts still being handed over — marked CONTRACT below:
 *   (KB)  Step 2 ingestion path (URL-crawl endpoint vs sync+contentPath).
 *   (GO)  Step 5 tenant-facing "widget seen on your domain" signal (the backoffice
 *         embed-origins endpoint is PM-evidence only, not tenant-facing).
 * They are stubbed walkable so the shell lands and the flow is completable end-to-end.
 */

type Skill = 'SUPPORT' | 'SALES' | 'CUSTOMER_SUCCESS';

interface VirtualAgent {
  id: string;
  name: string;
  nickname: string;
  skill: string;
  isPublished: boolean;
  contentPath?: string | null;
}

// Step labels are i18n KEYS, not copy: this array lives at module scope where no hook can
// reach, so the text is resolved inside ProgressBar instead. Order is the wizard's order.
const STEP_KEYS = ['workspace', 'agent', 'knowledge', 'test', 'install', 'goLive'] as const;

const card = 'bg-surface rounded-3xl p-8 shadow-2xl border border-white/5';
const input =
  'w-full bg-canvas border border-white/10 rounded-xl px-4 py-3 text-text-strong focus:border-primary outline-none transition-colors';
const primaryBtn =
  'w-full bg-primary text-white font-bold py-4 rounded-2xl hover:bg-primary-dark transition-all disabled:opacity-50';
const ghostBtn = 'text-sm text-gray-400 hover:text-text-strong transition-colors';

export const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation('common');
  const { user, accountId, refresh } = useAuth();
  const navigate = useNavigate();

  // Resume guard (eng-1 review): if a workspace already exists (accountId set — a mid-wizard
  // refresh after Step-0 created the account, or re-entry via /onboarding) START PAST Step 0.
  // WITHOUT this, WorkspaceStep re-POSTs /api/signup unconditionally and creates a DUPLICATE
  // Account (+AccountUser+re-seeded agents), orphaning the first — a data bug, not just lost
  // progress. accountId is settled here: ProtectedRoute gates children on !loading, and
  // /api/me resolves accountId before that. (useState initializer runs once, so advancing
  // past Step 0 mid-flow never re-triggers this.)
  const [step, setStep] = useState(accountId ? 1 : 0);
  const [agents, setAgents] = useState<VirtualAgent[]>([]);
  const [agentId, setAgentId] = useState<string | null>(null);
  // Option C — pricing IN the flow. "View plans" opens PlansPage as a non-blocking modal (the
  // wizard stays mounted underneath — never a pricing STEP/wall). The card-offer at the aha reuses
  // the G1 checkout with returnPath=/onboarding, so Stripe redirects back here with ?billing=…;
  // we surface a one-time banner and keep the user moving forward (never a gate).
  const [showPlans, setShowPlans] = useState(false);
  const billingReturn = new URLSearchParams(window.location.search).get('billing'); // 'success' | 'cancelled' | null
  const [cardBanner, setCardBanner] = useState<'success' | 'cancelled' | null>(
    billingReturn === 'success' || billingReturn === 'cancelled' ? billingReturn : null,
  );
  // F3 — the pricing banner asserts a fact ("you're on the 14-day Pro trial"), so it may only
  // render once the server says that fact is TRUE. Server-authoritative, never inferred from the
  // step index: before Step 0 stamps the account+trial there is no plan to speak of, and once a
  // card is added the account leaves TRIAL and the claim becomes false (and would sit right next
  // to the "🎉 Card added" banner). null = not yet known → render nothing.
  const [planNow, setPlanNow] = useState<{ plan?: string; planStatus?: string } | null>(null);
  useEffect(() => {
    if (!accountId) { setPlanNow(null); return; }   // pre-Step-0: nothing stamped yet
    let cancelled = false;
    fetch('/api/account/inference-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setPlanNow(d ? { plan: d.plan, planStatus: d.planStatus } : null); })
      .catch(() => { if (!cancelled) setPlanNow(null); });
    return () => { cancelled = true; };
    // Re-read after a checkout return: a successful card flips the plan off TRIAL.
  }, [accountId, billingReturn]);
  const onTrial = planNow?.plan === 'TRIAL' && planNow?.planStatus === 'trialing';

  // RESUMABILITY (Phase-2, on top of the live C0 session store): a returning tenant resumes at
  // the step they actually reached, derived from server state — not just "skip Step 0". Gated to
  // an account that already existed AT MOUNT, so it never disturbs the fresh-signup flow (Step 0
  // creating the account changes accountId, but this must not re-fire and jump the new user).
  const hadAccountAtMount = useRef(!!accountId);
  const [resuming, setResuming] = useState(hadAccountAtMount.current);

  const next = () => setStep((s) => Math.min(s + 1, STEP_KEYS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const loadAgents = async () => {
    const res = await fetch('/api/virtual-agents');
    if (res.ok) {
      const list: VirtualAgent[] = await res.json();
      setAgents(list);
      return list;
    }
    return [];
  };

  useEffect(() => {
    if (!hadAccountAtMount.current) return; // fresh user → normal Step-0 flow
    let cancelled = false;
    (async () => {
      const list = await loadAgents();
      if (cancelled) return;
      const published = list.find((a) => a.isPublished);
      const withKb = list.find((a) => a.contentPath);
      // Resolve an agent so post-redirect steps (Go-live) have one; the aha pick is lost on the
      // full-page Stripe round-trip, so prefer published → withKb → first-seeded.
      const chosen = published ?? withKb ?? list[0];
      if (chosen) setAgentId(chosen.id);
      if (billingReturn) {
        // Returned from the card-offer checkout. The user already hit the aha (Test) — keep them
        // moving FORWARD: success → Install (card added, continue setup); cancelled → back at Test
        // (declined, continue on the no-card trial). Never bounce to pick-skill.
        setStep(billingReturn === 'success' ? 4 : 3);
      } else if (published) setStep(5);                                // already went live → go-live
      else if (withKb) setStep(3);                                     // KB added → resume at Test
      else setStep(1);                                                 // resume at pick-skill
      setResuming(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (resuming) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-text-strong flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-xl">
        <ProgressBar step={step} onGo={setStep} />
        {/* F3: only while the account is genuinely on the trial. `cardBanner !== 'success'` is the
            race guard — the Stripe redirect can beat its own webhook, so the plan may still read
            TRIAL for a moment after a card is added; never show "no card needed" beside "Card added". */}
        {onTrial && cardBanner !== 'success' && <TrialPricingBanner onViewPlans={() => setShowPlans(true)} />}
        {cardBanner && (
          <div
            className={`mt-4 rounded-2xl px-4 py-3 text-sm flex items-center justify-between gap-3 ${
              cardBanner === 'success'
                ? 'bg-green-900/30 border border-green-700/60 text-green-100'
                : 'bg-surface border border-white/10 text-gray-300'
            }`}
          >
            <span>
              {cardBanner === 'success'
                ? t('onboarding.cardBanner.success')
                : t('onboarding.cardBanner.cancelled')}
            </span>
            <button onClick={() => setCardBanner(null)} aria-label={t('onboarding.cardBanner.dismiss')} className="text-gray-400 hover:text-text-strong">✕</button>
          </div>
        )}
        <div className="mt-8">
          {step === 0 && (
            <WorkspaceStep
              defaultBillingEmail={user?.email || ''}
              hasAccount={!!accountId}
              onDone={async () => {
                await refresh();
                await loadAgents();
                next();
              }}
            />
          )}
          {step === 1 && (
            <AgentStep
              agents={agents}
              onPick={(id) => {
                setAgentId(id);
                next();
              }}
              onLoad={loadAgents}
            />
          )}
          {step === 2 && <KnowledgeStep agentId={agentId} onNext={next} onBack={back} />}
          {step === 3 && <TestStep agentId={agentId} onNext={next} onBack={back} />}
          {step === 4 && <InstallStep accountId={accountId} onNext={next} onBack={back} />}
          {step === 5 && (
            <GoLiveStep
              agentId={agentId}
              onDone={() => navigate('/')}
              onBack={back}
            />
          )}
        </div>
      </div>
      {showPlans && <PlansModal onClose={() => setShowPlans(false)} />}
    </div>
  );
};

// Non-blocking pricing visibility (Option C §1). A calm banner — NOT a step, NOT a wall — that
// keeps the trial promise front-and-centre and links to the one pricing source of truth (PlansPage).
// COPY IS PLUGGABLE (GTM is drafting the final F1/F4 wording under PM ruling): swap the strings
// here — `lead`/`emphasis`/`tail` compose one sentence, `cta` labels the link. Changing wording
// must not require touching the render or the F3 gate above.
// Was TRIAL_BANNER_COPY, a sentence pre-split into lead/emphasis/tail. That shape cannot be
// translated: the emphasised span does not sit in the same place in every language. It is now one
// key with an inline <strong>, so a translator moves the emphasis wherever the sentence needs it.
const TrialPricingBanner: React.FC<{ onViewPlans: () => void }> = ({ onViewPlans }) => {
  const { t } = useTranslation('common');
  return (
  <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-surface border border-white/5 px-4 py-3">
    <span className="text-sm text-gray-300">
      <Trans
        i18nKey="onboarding.trialBanner.text"
        components={{ strong: <span className="text-text-strong font-semibold" /> }}
      />
    </span>
    <button onClick={onViewPlans} className="text-sm text-primary font-semibold hover:underline whitespace-nowrap">
      {t('onboarding.trialBanner.cta')}
    </button>
  </div>
  );
};

// "View plans" modal — reuses PlansPage verbatim (ONE pricing source of truth) inside a dismissible
// overlay so the wizard is never unmounted and no onboarding progress is lost. Non-blocking by
// construction: closing returns the user exactly where they were.
const PlansModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation('common');
  // F2: upgrading from INSIDE the wizard must come back to the wizard. Without this the page's
  // own checkout defaults to returnPath=/plans and the user is dumped out of onboarding mid-flow.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-nav/70 overflow-y-auto" role="dialog" aria-modal="true" onClick={onClose}>
      <button
        onClick={onClose}
        aria-label={t('onboarding.plansModal.close')}
        className="fixed top-4 right-4 z-[51] w-10 h-10 rounded-full bg-surface border border-white/10 text-gray-300 hover:text-text-strong text-lg"
      >
        ✕
      </button>
      {/* Stop propagation so clicks inside the plans content don't dismiss the modal. */}
      <div onClick={(e) => e.stopPropagation()}>
        <PlansPage returnPath="/onboarding" />
      </div>
    </div>
  );
};

// Stepper: numbered dots per step; completed/current highlighted, and completed steps are
// clickable to jump BACK (forward steps stay disabled — you complete in order). Labels hide on
// mobile to keep the row compact.
const ProgressBar: React.FC<{ step: number; onGo: (s: number) => void }> = ({ step, onGo }) => {
  const { t } = useTranslation('common');
  return (
  <div>
    <div className="flex items-start justify-between mb-3 gap-1">
      {STEP_KEYS.map((key, i) => {
        const done = i < step;
        const current = i === step;
        const reachable = i <= step;
        return (
          <button
            key={key}
            type="button"
            onClick={() => reachable && onGo(i)}
            disabled={!reachable}
            aria-current={current ? 'step' : undefined}
            className={`flex flex-col items-center gap-1 flex-1 ${reachable ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
                current ? 'bg-primary text-white' : done ? 'bg-primary/25 text-primary' : 'bg-canvas text-gray-600'
              }`}
            >
              {done ? '✓' : i + 1}
            </div>
            <span className={`text-[10px] hidden sm:block ${current ? 'text-primary font-semibold' : 'text-gray-600'}`}>{t(`onboarding.steps.${key}`)}</span>
          </button>
        );
      })}
    </div>
    <div className="w-full bg-canvas h-1 rounded-full overflow-hidden">
      <div
        className="bg-primary h-full transition-all duration-300"
        style={{ width: `${((step + 1) / STEP_KEYS.length) * 100}%` }}
      />
    </div>
  </div>
  );
};

// ── Step 0 · Create workspace ────────────────────────────────────────────────
// Reframed /api/signup: company + website only. Phone/billing dropped from the critical
// path (moved to trial-end). Backend stamps the trial quota (F3) + inits agents + assigns
// the managed model silently — the wizard shows none of that.
const WorkspaceStep: React.FC<{ defaultBillingEmail: string; hasAccount: boolean; onDone: () => void }> = ({
  defaultBillingEmail,
  hasAccount,
  onDone,
}) => {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('common');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Belt-and-suspenders (eng-1 review): never re-create if a workspace already exists — the
    // step-init guard normally means this step doesn't render for an account-holder, but if it
    // somehow does, advance instead of POSTing a duplicate Account.
    if (hasAccount) { onDone(); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // billingEmail defaulted from the Google identity; phone/billingDetails intentionally
        // omitted — not on the AC3 critical path.
        body: JSON.stringify({ name, website, billingEmail: defaultBillingEmail }),
      });
      if (!res.ok) throw new Error(t('onboarding.workspace.errCreate'));
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={card}>
      <h1 className="text-3xl font-bold mb-2">{t('onboarding.workspace.title')}</h1>
      <p className="text-gray-400 mb-8">{t('onboarding.workspace.subtitle')}</p>
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-400">{t('onboarding.workspace.companyName')}</label>
          <input required className={input} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-400">{t('onboarding.workspace.website')}</label>
          <input
            required
            type="url"
            placeholder="https://…"
            className={input}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <p className="text-xs text-gray-600">{t('onboarding.workspace.websiteHint')}</p>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button disabled={loading} type="submit" className={primaryBtn}>
          {loading ? t('onboarding.workspace.submitBusy') : t('onboarding.workspace.submit')}
        </button>
      </div>
    </form>
  );
};

// ── Step 1 · Pick your agent's job ───────────────────────────────────────────
// Keys, not copy — module scope again. The Record<Skill, …> shape is kept deliberately: it is what
// makes adding a Skill a COMPILE error here rather than a silently unlabelled button at runtime.
const SKILL_LABELS: Record<Skill, { titleKey: string; blurbKey: string }> = {
  SUPPORT: { titleKey: 'onboarding.agent.skills.support.title', blurbKey: 'onboarding.agent.skills.support.blurb' },
  SALES: { titleKey: 'onboarding.agent.skills.sales.title', blurbKey: 'onboarding.agent.skills.sales.blurb' },
  CUSTOMER_SUCCESS: { titleKey: 'onboarding.agent.skills.customerSuccess.title', blurbKey: 'onboarding.agent.skills.customerSuccess.blurb' },
};

const AgentStep: React.FC<{
  agents: VirtualAgent[];
  onPick: (agentId: string) => void;
  onLoad: () => Promise<VirtualAgent[]>;
}> = ({ agents, onPick, onLoad }) => {
  const [list, setList] = useState<VirtualAgent[]>(agents);
  const { t } = useTranslation('common');

  useEffect(() => {
    if (agents.length === 0) onLoad().then(setList);
    else setList(agents);
  }, [agents, onLoad]);

  const pickSkill = (skill: Skill) => {
    const match = list.find((a) => a.skill === skill);
    if (match) onPick(match.id);
  };

  return (
    <div className={card}>
      <h1 className="text-3xl font-bold mb-2">{t('onboarding.agent.title')}</h1>
      <p className="text-gray-400 mb-8">{t('onboarding.agent.subtitle')}</p>
      <div className="space-y-3">
        {(Object.keys(SKILL_LABELS) as Skill[]).map((skill) => (
          <button
            key={skill}
            onClick={() => pickSkill(skill)}
            className="w-full text-left bg-canvas border border-white/10 rounded-2xl p-5 hover:border-primary/60 transition-all"
          >
            <div className="font-bold">{t(SKILL_LABELS[skill].titleKey)}</div>
            <div className="text-sm text-gray-400">{t(SKILL_LABELS[skill].blurbKey)}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Step 2 · Add knowledge ───────────────────────────────────────────────────
// CONTRACT 1 (eng-1): no URL-crawl / upload today. MVP = PASTE-TEXT → a net-new TENANT
// endpoint POST /api/virtual-agents/:id/kb {text} that writes to the per-account contentPath
// and runs assertCustomerSafeKB() BEFORE accepting (refuses internal-financials-shaped content
// with a clear message we surface). Persona-only (skip) is also a valid zero-config bot — so
// this step is fully skippable. Whether it's in the MVP AC3 flow at all is PM's scope call.
const KnowledgeStep: React.FC<{ agentId: string | null; onNext: () => void; onBack: () => void }> = ({
  agentId,
  onNext,
  onBack,
}) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const { t } = useTranslation('common');

  const save = async () => {
    if (!agentId || !text.trim()) return;
    setStatus('working');
    setMessage(null);
    try {
      const res = await fetch(`/api/virtual-agents/${agentId}/kb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        setStatus('done');
      } else {
        // The customer-safe guardrail refuses with a reason — show it, keep them on-step.
        const body = await res.json().catch(() => ({}));
        setMessage(body.error || t('onboarding.knowledge.errRefused'));
        setStatus('error');
      }
    } catch {
      setMessage(t('onboarding.knowledge.errSave'));
      setStatus('error');
    }
  };

  return (
    <div className={card}>
      <h1 className="text-3xl font-bold mb-2">{t('onboarding.knowledge.title')}</h1>
      <p className="text-gray-400 mb-6">
        {t('onboarding.knowledge.body')}
      </p>
      <textarea
        rows={7}
        className={input + ' font-sans resize-none'}
        placeholder={t('onboarding.knowledge.placeholder')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {status === 'done' ? (
        <p className="text-sm text-primary mt-3">{t('onboarding.knowledge.added')}</p>
      ) : (
        <button onClick={save} disabled={status === 'working' || !text.trim()} className={primaryBtn + ' mt-4'}>
          {status === 'working' ? t('onboarding.knowledge.addBusy') : t('onboarding.knowledge.add')}
        </button>
      )}
      {status === 'error' && message && <p className="text-sm text-amber-400 mt-3">{message}</p>}
      <div className="flex justify-between items-center mt-6">
        <button onClick={onBack} className={ghostBtn}>
          {t('onboarding.nav.back')}
        </button>
        <button onClick={onNext} className={ghostBtn}>
          {status === 'done' ? t('onboarding.nav.next') : t('onboarding.nav.skip')}
        </button>
      </div>
    </div>
  );
};

// ── Step 3 · Test it (reuse test-chat stream) ────────────────────────────────
// The Tier-1 aha. Streams from the existing /virtual-agents/:id/test-chat. The degradation
// console (InferenceStatusBanner) renders inline here so an outage/cap never looks like a hang.
const TestStep: React.FC<{ agentId: string | null; onNext: () => void; onBack: () => void }> = ({
  agentId,
  onNext,
  onBack,
}) => {
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const { t } = useTranslation('common');
  const [sending, setSending] = useState(false);
  // Option C §2 — the AHA is First-Real-Reply: the first time the test-chat streams back a real
  // agent answer ("it works!"). We latch it once and show a SKIPPABLE card-offer at peak intent.
  const [ahaReached, setAhaReached] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!msg.trim() || !agentId || sending) return;
    const userMsg = msg;
    setMsg('');
    setHistory((h) => [...h, { role: 'user', content: userMsg }]);
    setSending(true);
    try {
      const res = await fetch(`/api/virtual-agents/${agentId}/test-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, history }),
      });
      if (!res.body) throw new Error('no stream');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let answer = '';
      setHistory((h) => [...h, { role: 'assistant', content: '' }]);
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value, { stream: true }).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) answer += data.chunk;
            if (data.done) answer = data.answer;
            setHistory((h) => {
              const n = [...h];
              n[n.length - 1].content = answer;
              return n;
            });
          } catch {
            /* partial json */
          }
        }
      }
      // First-Real-Reply reached (non-empty answer, no error thrown) → the aha. Latch once.
      if (answer.trim()) setAhaReached(true);
    } catch {
      // No silent fallback: surface a degraded state instead of a hang. The banner (below)
      // renders the actionable cause; here we drop a soft line into the thread.
      window.dispatchEvent(new Event('inference:error'));
      setHistory((h) => [...h, { role: 'assistant', content: t('onboarding.test.errDegraded') }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={card}>
      <InferenceStatusBanner />
      <div className="flex items-baseline justify-between mb-2">
        <h1 className="text-3xl font-bold">{t('onboarding.test.title')}</h1>
        <span className="text-xs text-gray-500">{t('onboarding.test.poweredBy')}</span>
      </div>
      <p className="text-gray-400 mb-6">{t('onboarding.test.subtitle')}</p>
      <div className="bg-canvas border border-white/10 rounded-2xl p-4 h-64 overflow-y-auto space-y-3">
        {history.length === 0 ? (
          <p className="text-gray-600 text-sm text-center mt-24">{t('onboarding.test.examplePrompt')}</p>
        ) : (
          history.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                  m.role === 'user' ? 'bg-primary text-white' : 'bg-canvas text-gray-100'
                }`}
              >
                {m.content || '…'}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-4">
        <input
          className={input}
          placeholder={t('onboarding.test.inputPlaceholder')}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          disabled={sending}
        />
        <button type="submit" disabled={sending || !msg.trim()} className="px-5 rounded-xl bg-primary text-white font-bold disabled:opacity-50">
          {t('onboarding.test.send')}
        </button>
      </form>
      {ahaReached && !offerDismissed && <CardOfferAtAha onDismiss={() => setOfferDismissed(true)} />}
      <div className="flex justify-between items-center mt-6">
        <button onClick={onBack} className={ghostBtn}>
          {t('onboarding.nav.back')}
        </button>
        <button onClick={onNext} disabled={history.length === 0} className={primaryBtn + ' !w-auto px-8'}>
          {t('onboarding.test.looksGood')}
        </button>
      </div>
    </div>
  );
};

// Skippable card-offer at the aha (Option C §2). Reuses the G1-verified POST /api/billing/checkout
// {planId:"PRO"} with returnPath=/onboarding so Stripe returns into the wizard. It is an OFFER, not
// a gate: the "Looks good → Install" button above stays fully usable, and "Maybe later" dismisses.
// Trial model is UNCHANGED — declining continues the no-card 14-day trial.
const CardOfferAtAha: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { t } = useTranslation('common');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addCard = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: 'PRO', returnPath: '/onboarding' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || t('onboarding.cardOffer.errCheckout'));
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
      else throw new Error(t('onboarding.cardOffer.errUnavailable'));
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-primary/40 bg-primary/[0.06] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-bold text-text-strong">{t('onboarding.cardOffer.title')}</div>
          <p className="text-sm text-gray-300 mt-1">
            <Trans
              i18nKey="onboarding.cardOffer.body"
              components={{ strong: <span className="font-semibold" /> }}
            />
          </p>
        </div>
        <button onClick={onDismiss} aria-label={t('onboarding.cardOffer.dismiss')} className="text-gray-500 hover:text-text-strong">✕</button>
      </div>
      {error && <p className="text-sm text-amber-400 mt-2">{error}</p>}
      <div className="flex items-center gap-4 mt-3">
        <button
          onClick={addCard}
          disabled={busy}
          className="px-5 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark transition-all disabled:opacity-50"
        >
          {busy ? t('onboarding.cardOffer.addBusy') : t('onboarding.cardOffer.add')}
        </button>
        <button onClick={onDismiss} className="text-sm text-gray-400 hover:text-text-strong">
          {t('onboarding.cardOffer.later')}
        </button>
      </div>
    </div>
  );
};

// ── Step 4 · Install the widget ──────────────────────────────────────────────
const InstallStep: React.FC<{ accountId: string | null; onNext: () => void; onBack: () => void }> = ({
  accountId,
  onNext,
  onBack,
}) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation('common');
  const origin = window.location.origin;
  // Was `${origin}/chat-widget.js` — one missing path segment, and the SPA catch-all served the
  // 462-byte app shell as JavaScript instead of 404ing. See src/widget/snippet.ts.
  const snippet = widgetSnippet(origin, accountId);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  };

  return (
    <div className={card}>
      <h1 className="text-3xl font-bold mb-2">{t('onboarding.install.title')}</h1>
      <p className="text-gray-400 mb-2">{t('onboarding.install.subtitle')}</p>
      {/*
        This step used to end at the sentence above, which reads as a COMPLETED instruction — paste
        it and you are done. Publishing happens at Go-live, one step later, and a freshly created
        agent is unpublished by default. So a partner who pasted the snippet and stopped had a live
        widget that accepted messages and answered none of them, in the window this wizard itself
        creates. Saying so here costs one sentence; discovering it costs a customer.
      */}
      <p className="text-sm text-primary mb-6">
        <Trans i18nKey="onboarding.install.notYetLive" components={{ strong: <strong /> }} />
      </p>
      <div className="bg-canvas border border-white/10 rounded-2xl p-4 font-mono text-xs text-gray-300 break-all">
        {snippet}
      </div>
      <button onClick={copy} className={primaryBtn + ' mt-4'}>
        {copied ? t('onboarding.install.copied') : t('onboarding.install.copy')}
      </button>
      {/*
        This used to be a <button onClick={onNext}> labelled "Email these steps to your developer".
        There was no email implementation anywhere in the codebase — it silently advanced the
        wizard, so the one user who most needed help (on a phone, unable to edit their site) got a
        step skipped instead of an email. Same class as the Developer Login button: UI advertising
        a capability that does not exist.

        Implemented as a mailto: rather than a server-side send, deliberately. It genuinely does
        what the label says, works offline, needs no new authenticated endpoint, and — the reason
        that settles it — a server-side version would take an arbitrary recipient address from the
        browser and put it in a To: header, which is a header-injection surface and an open-relay
        shape we would have to defend. There is nothing to defend here: the user's own mail client
        addresses the message, so we never handle the address at all.
      */}
      <p className="text-xs text-gray-600 text-center mt-3">
        <Trans
          i18nKey="onboarding.install.emailDev"
          components={{
            mail: <a href={mailtoInstallSteps(origin, accountId, snippet)} className="text-primary hover:underline" />,
          }}
        />
      </p>
      <div className="flex justify-between items-center mt-6">
        <button onClick={onBack} className={ghostBtn}>
          {t('onboarding.nav.back')}
        </button>
        <button onClick={onNext} className={ghostBtn}>
          {t('onboarding.nav.added')}
        </button>
      </div>
    </div>
  );
};

// ── Step 5 · Go live & verify ────────────────────────────────────────────────
// Publishes the chosen agent (reuses the publish toggle; publishing unpublishes siblings, so
// this is THE live agent). CONTRACT (eng-1): the tenant-facing "widget seen on your domain"
// signal is being handed over — the backoffice embed-origins endpoint is PM-evidence only.
// Detection is stubbed optimistic here so the shell is walkable; swap in the poll when it lands.
const GoLiveStep: React.FC<{ agentId: string | null; onDone: () => void; onBack: () => void }> = ({
  agentId,
  onDone,
  onBack,
}) => {
  const { t } = useTranslation('common');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);        // agent published; now detecting on domain
  const [detected, setDetected] = useState<{ origin?: string } | null>(null); // widget seen on their site
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // Real Tier-2 live-detect (eng-1 §1c): poll the tenant embed-status until the widget is seen on
  // the customer's own domain. It only turns true once a real visitor loads the widget, so this
  // is non-blocking — the user can head to the dashboard while detection is pending.
  const startPolling = () => {
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const res = await fetch('/api/account/embed-status');
        if (res.ok) {
          const s = await res.json(); // { live, origin?, lastSeenAt? }
          if (s.live) { setDetected({ origin: s.origin }); return; }
        }
      } catch { /* keep polling */ }
      if (tries < 30) pollRef.current = setTimeout(poll, 4000); // ~2 min of gentle polling
    };
    poll();
  };

  const goLive = async () => {
    if (!agentId) return;
    setPublishing(true);
    setError(null);
    try {
      // Fetch the agent first so the PATCH carries a complete body (the endpoint reads named
      // fields). Then flip isPublished — siblings auto-unpublish server-side.
      const list: VirtualAgent[] = await (await fetch('/api/virtual-agents')).json();
      const agent = list.find((a) => a.id === agentId);
      const res = await fetch(`/api/virtual-agents/${agentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...agent, isEnabled: true, isPublished: true }),
      });
      if (!res.ok) throw new Error(t('onboarding.goLive.errPublish'));
      setPublished(true);
      startPolling();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  };

  // Detected live on their domain → the Tier-2 activation moment.
  if (detected) {
    return (
      <div className={card + ' text-center'}>
        <div className="text-5xl mb-4">🎉</div>
        <h1 className="text-3xl font-bold mb-2">{t('onboarding.goLive.liveTitle')}</h1>
        <p className="text-gray-400 mb-8">
          {detected.origin
            ? t('onboarding.goLive.liveOnOrigin', { origin: detected.origin })
            : t('onboarding.goLive.liveOnSite')}
        </p>
        <button onClick={onDone} className={primaryBtn}>
          {t('onboarding.goLive.dashboard')}
        </button>
      </div>
    );
  }

  // Published, waiting for the widget to be seen on their domain (needs a real visitor).
  if (published) {
    return (
      <div className={card + ' text-center'}>
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary mx-auto mb-5" />
        <h1 className="text-3xl font-bold mb-2">{t('onboarding.goLive.publishedTitle')}</h1>
        <p className="text-gray-400 mb-8">
          {t('onboarding.goLive.publishedBody')}
        </p>
        <button onClick={onDone} className={primaryBtn}>
          {t('onboarding.goLive.dashboard')}
        </button>
      </div>
    );
  }

  return (
    <div className={card}>
      <h1 className="text-3xl font-bold mb-2">{t('onboarding.goLive.title')}</h1>
      <p className="text-gray-400 mb-8">
        {t('onboarding.goLive.body')}
      </p>
      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}
      <button onClick={goLive} disabled={publishing} className={primaryBtn}>
        {publishing ? t('onboarding.goLive.publishBusy') : t('onboarding.goLive.publish')}
      </button>
      <div className="mt-6">
        <button onClick={onBack} className={ghostBtn}>
          {t('onboarding.nav.back')}
        </button>
      </div>
    </div>
  );
};

export default OnboardingWizard;
